import { trackProps } from '../../helpers/utils/analytics';
import { SimpleGrid } from '@chakra-ui/react';
import { Delete, Infinity as InfinityIcon } from 'lucide-react';
import { Button } from './button';
import { Text } from './text';

export interface NumberKeypadProps {
  /** Called with the pressed digit ('0'–'9'). */
  onDigit: (digit: string) => void;
  /** Optional backspace action; the key is always rendered. */
  onBackspace?: () => void;
  /**
   * Optional "no limit" action (e.g. an ∞ cap). The key is rendered only when
   * provided — entry points that must produce a number omit it.
   */
  onInfinity?: () => void;
}

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * Custom numeric keypad (the same pattern as the bid/made ButtonGrid) used
 * instead of the native numeric input, so extra keys like ∞ can live directly
 * on the pad without fighting the OS keyboard.
 */
function NumberKeypad({ onDigit, onBackspace, onInfinity }: NumberKeypadProps) {
  // Digits are Emerald/Picker keys at 56pt; ∞ and ⌫ are Emerald/Keypad keys
  // (Emerald/Limit keypad 1979:196252, Regular size).
  return (
    <SimpleGrid
      columns={3}
      gap="var(--limit-key-gap, 8px)"
      className="limit-keypad"
    >
      {DIGITS.map((digit) => (
        <Button
          key={digit}
          variant="pickerKey"
          h="var(--limit-key-height, 56px)"
          minH="var(--limit-key-height, 56px)"
          {...trackProps('keypad_digit_pressed', { context: 'score_limit' })}
          onClick={() => onDigit(digit)}
          data-testid={`keypad-${digit}`}
        >
          {digit}
        </Button>
      ))}
      {onInfinity && (
        <Button
          {...trackProps('keypad_no_limit_selected', {
            context: 'score_limit',
          })}
          onClick={onInfinity}
          variant="pickerKey"
          h="var(--limit-key-height, 56px)"
          minH="var(--limit-key-height, 56px)"
          data-testid="keypad-infinity"
          flexDirection="column"
          gap="1px"
        >
          <InfinityIcon size={26} aria-hidden="true" />
          <Text
            fontSize="var(--spades-font-size-3xs)"
            fontWeight="700"
            lineHeight={1.25}
            letterSpacing="var(--spades-tracking-lg)"
            textTransform="uppercase"
            color="var(--spades-muted)"
          >
            No limit
          </Text>
        </Button>
      )}
      <Button
        variant="pickerKey"
        h="var(--limit-key-height, 56px)"
        minH="var(--limit-key-height, 56px)"
        {...trackProps('keypad_digit_pressed', { context: 'score_limit' })}
        onClick={() => onDigit('0')}
        data-testid="keypad-0"
      >
        0
      </Button>
      <Button
        variant="pickerKey"
        h="var(--limit-key-height, 56px)"
        minH="var(--limit-key-height, 56px)"
        {...trackProps('keypad_backspace_pressed', { context: 'score_limit' })}
        onClick={onBackspace}
        data-testid="keypad-backspace"
        aria-label="Backspace"
      >
        <Delete size={22} aria-hidden="true" />
      </Button>
    </SimpleGrid>
  );
}

export default NumberKeypad;
